import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { confirmDelete } from '../utils/confirmDelete';
import ModalPortal from '../components/ModalPortal';
import { formatIndiaTime, isPastIndiaTime } from '../config/india-timezone';
import cronParser from 'cron-parser';
import CampaignProgressStatus from '../components/CampaignProgressStatus';
import { useCampaignProgress } from '../hooks/useCampaignProgress';
import {
  Play,
  Pause,
  Plus,
  Edit,
  Trash2,
  MessageSquare,
  BarChart3,
  Calendar,
  X,
  Clock,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square
} from 'lucide-react';

// Format timestamp in local timezone
const formatLocalTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short'
  });
};

const Flows = () => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  // Debug: Log every render
  console.log('🔍 Flows component render - Workspace ID:', currentWorkspace?.id, 'Name:', currentWorkspace?.name);
  const [flowLogs, setFlowLogs] = useState(null);
  const [showFlowLogs, setShowFlowLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(20);
  const [deletingLogs, setDeletingLogs] = useState(false);

  // New state for improved table functionality
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedFlows, setSelectedFlows] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Countdown timer state
  const [countdowns, setCountdowns] = useState({});

  const { getProgress } = useCampaignProgress(
    'whatsapp',
    currentWorkspace?.id,
    queryClient,
    ['flows', currentWorkspace?.id, pagination.page, searchTerm, statusFilter]
  );

  // Fetch flows using React Query
  const { data: flowsData, isLoading: loading } = useQuery(
    ['flows', currentWorkspace?.id, pagination.page, searchTerm, statusFilter],
    async () => {
      if (!currentWorkspace?.id) {
        return { success: true, flows: [], pagination: { page: 1, limit: 20, total: 0, pages: 0 } };
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter })
      };

      const response = await apiClient.get('/api/flows', { headers, params });

      // Update pagination from response
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }

      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('Error fetching flows:', error);
        toast.error('Failed to fetch campaigns');
      }
    }
  );

  const flows = useMemo(() => flowsData?.flows || [], [flowsData?.flows]);

  // Calculate next execution time for a flow
  const calculateNextExecution = useCallback((flow) => {


    if (!flow.schedule_config || flow.status !== 'active') {

      return null;
    }

    // Parse schedule_config if it's a JSON string
    let scheduleConfig = flow.schedule_config;
    if (typeof scheduleConfig === 'string') {
      try {
        scheduleConfig = JSON.parse(scheduleConfig);
      } catch (error) {
        console.error('Error parsing schedule_config JSON:', error);
        return null;
      }
    }

    const { schedule_type, schedule_time, cron_expr } = scheduleConfig;
    const now = new Date();

    if (schedule_type === 'once') {
      const scheduledTime = new Date(schedule_time);
      return scheduledTime > now ? scheduledTime : null;
    } else if (schedule_type === 'cron' && cron_expr) {
      // For cron expressions, use cron-parser library with UTC timezone
      try {
        const interval = cronParser.parseExpression(cron_expr, {
          tz: 'UTC'
        });
        const nextExecution = interval.next().toDate();



        return nextExecution;
      } catch (error) {
        console.error('Error parsing cron expression:', error);
        return null;
      }
    }

    return null;
  }, []);

  // Update all countdowns
  const updateAllCountdowns = useCallback(() => {
    const newCountdowns = {};

    flows.forEach(flow => {
      if (flow.status === 'active' && flow.trigger_type === 'scheduled' && flow.schedule_config) {
        const nextExecution = calculateNextExecution(flow);

        if (nextExecution) {
          const now = new Date();
          const timeDiff = nextExecution.getTime() - now.getTime();

          if (timeDiff > 0) {
            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            newCountdowns[flow.id] = {
              days,
              hours,
              minutes,
              seconds,
              nextExecution
            };
          }
        }
      }
    });

    setCountdowns(newCountdowns);
  }, [flows, calculateNextExecution]);

  // Countdown timer effect
  useEffect(() => {
    updateAllCountdowns();

    const interval = setInterval(() => {
      updateAllCountdowns();
    }, 1000);

    return () => clearInterval(interval);
  }, [updateAllCountdowns]);


  // Fetch flows




  // Fetch flow logs
  const fetchFlowLogs = async (flowId, page = 1) => {
    try {
      setLogsLoading(true);
      setSelectedFlowId(flowId);
      setCurrentPage(page);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.get(`/api/flows/${flowId}/logs?page=${page}&limit=${logsPerPage}`, { headers });
      setFlowLogs(response.data);
      setShowFlowLogs(true);
    } catch (error) {
      console.error('Error fetching flow logs:', error);
      toast.error('Failed to fetch flow logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Delete specific log
  const deleteLog = async (logId) => {
    if (!confirmDelete('Are you sure you want to delete this log?')) {
      return;
    }

    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.delete(`/api/flows/logs/${logId}`, { headers });
      toast.success('Log deleted successfully');
      // Refresh logs
      fetchFlowLogs(selectedFlowId, currentPage);
    } catch (error) {
      console.error('Error deleting log:', error);
      toast.error('Failed to delete log');
    }
  };

  // Delete all logs for current flow
  const deleteAllFlowLogs = async () => {
    if (!confirmDelete(`Are you sure you want to delete ALL logs for this campaign? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingLogs(true);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.delete(`/api/flows/${selectedFlowId}/logs`, { headers });
      toast.success(`Deleted ${response.data.deletedCount} logs successfully`);
      // Refresh logs
      fetchFlowLogs(selectedFlowId, 1);
    } catch (error) {
      console.error('Error deleting flow logs:', error);
      toast.error('Failed to delete logs');
    } finally {
      setDeletingLogs(false);
    }
  };





  // Delete flow
  const handleDeleteFlow = async (id) => {
    if (!confirmDelete('Are you sure you want to delete this campaign?')) return;

    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.delete(`/api/flows/${id}`, { headers });
      queryClient.invalidateQueries(['flows', currentWorkspace?.id]);
      toast.success('Campaign deleted successfully');
    } catch (error) {
      console.error('Error deleting flow:', error);
      toast.error('Failed to delete campaign');
    }
  };


  // Toggle flow status
  const handleToggleFlow = async (flow) => {
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      if (flow.trigger_type === 'scheduled') {
        // For scheduled flows, use specific activate/deactivate endpoints
        const endpoint = flow.status === 'active' ? 'deactivate' : 'activate';
        const response = await apiClient.post(`/api/flows/${flow.id}/${endpoint}`, {}, { headers });

        queryClient.invalidateQueries(['flows', currentWorkspace?.id]);
        toast.success(response.data.message);
      } else {
        // For other flows, use the general update endpoint
        const newStatus = flow.status === 'active' ? 'paused' : 'active';
        await apiClient.put(`/api/flows/${flow.id}`, { ...flow, status: newStatus }, { headers });
        queryClient.invalidateQueries(['flows', currentWorkspace?.id]);
        toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'}`);
      }
    } catch (error) {
      console.error('Error updating flow:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update flow';
      toast.error(errorMessage);
    }
  };

  // Duplicate flow
  // Bulk delete flows
  const handleBulkDelete = async () => {
    if (selectedFlows.length === 0) {
      toast.error('Please select campaigns to delete');
      return;
    }

    if (!confirmDelete(`Are you sure you want to delete ${selectedFlows.length} campaign(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      setBulkDeleting(true);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.delete('/api/flows/bulk', {
        headers,
        data: { flowIds: selectedFlows }
      });

      toast.success(`${selectedFlows.length} campaign(s) deleted successfully`);
      setSelectedFlows([]);
      queryClient.invalidateQueries(['flows', currentWorkspace?.id]);
    } catch (error) {
      console.error('Error bulk deleting flows:', error);
      toast.error('Failed to delete campaigns');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Handle individual flow selection
  const handleFlowSelect = (flowId) => {
    setSelectedFlows(prev =>
      prev.includes(flowId)
        ? prev.filter(id => id !== flowId)
        : [...prev, flowId]
    );
  };

  // Handle select all flows
  const handleSelectAll = () => {
    if (selectedFlows.length === flows.length) {
      setSelectedFlows([]);
    } else {
      setSelectedFlows(flows.map(flow => flow.id));
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status || 'draft') {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get trigger icon
  const getTriggerIcon = (trigger) => {
    switch (trigger) {
      case 'scheduled': return <Calendar className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  const parseFlowScheduleConfig = (flow) => {
    let cfg = flow.schedule_config;
    if (typeof cfg === 'string') {
      try {
        cfg = JSON.parse(cfg);
      } catch {
        return {};
      }
    }
    return cfg || {};
  };

  const isOnceScheduleCompleted = (flow) => {
    const cfg = parseFlowScheduleConfig(flow);
    if (cfg.schedule_type !== 'once') return false;
    if (flow.status === 'completed') return true;
    const progress = getProgress(flow);
    return progress?.status === 'completed';
  };

  const getFlowPlayPauseAction = (flow) => {
    const progress = getProgress(flow);
    if (progress?.status === 'running' || flow.status === 'active') {
      return { show: true, mode: 'pause' };
    }
    if (isOnceScheduleCompleted(flow)) {
      return { show: false, mode: null, title: 'One-time campaign already completed' };
    }
    return { show: true, mode: 'play' };
  };

  return (
    <div>
      {/* Unified Flows Dashboard */}
      <div className="bg-white rounded-xl shadow-sm border">
        {/* Header Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Whatsapp Campaigns</h1>
              <p className="text-gray-600 mt-2">Create automated message sequences for your contacts</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/flows/create')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-5 w-5" />
                <span>Create Campaign</span>
              </button>
            </div>
          </div>

          {/* Stats Cards - Inline */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Campaigns</p>
                  <p className="text-xl font-bold text-gray-900">{flows.length}</p>
                </div>
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                  <p className="text-xl font-bold text-green-600">
                    {flows.filter(f => f.status === 'active').length}
                  </p>
                </div>
                <Play className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Draft Campaigns</p>
                  <p className="text-xl font-bold text-gray-600">
                    {flows.filter(f => f.status === 'draft').length}
                  </p>
                </div>
                <Edit className="h-6 w-6 text-gray-600" />
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <p className="text-xl font-bold text-blue-600">
                    {flows.filter(f => f.status === 'completed').length}
                  </p>
                </div>
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedFlows.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedFlows.length} selected
                </span>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Flows Table Section */}
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Campaigns</h2>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading campaigns...</p>
            </div>
          ) : flows.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || statusFilter
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first automated message campaign to get started'
                }
              </p>
              {!searchTerm && !statusFilter && (
                <button
                  onClick={() => navigate('/flows/create')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Your First Campaign
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <button
                          onClick={handleSelectAll}
                          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                        >
                          {selectedFlows.length === flows.length ? (
                            <CheckSquare className="h-4 w-4" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                          Select All
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Trigger</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Schedule</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Created</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Created By</th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {flows.map((flow) => (
                      <tr key={flow.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleFlowSelect(flow.id)}
                            className="flex items-center gap-2"
                          >
                            {selectedFlows.includes(flow.id) ? (
                              <CheckSquare className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Square className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {flow.name || 'Unnamed Campaign'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {flow.description || 'No description'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <CampaignProgressStatus
                            status={flow.status}
                            progress={getProgress(flow)}
                            getStatusColor={getStatusColor}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            {getTriggerIcon(flow.trigger_type || 'scheduled')}
                            <span className="capitalize">{(flow.trigger_type || 'scheduled').replace('_', ' ')}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600">
                            {flow.trigger_type === 'scheduled' && flow.schedule_config ? (
                              <div>
                                {flow.schedule_config.schedule_type === 'once' ? (
                                  <div>
                                    <div>Once: {formatIndiaTime(flow.schedule_config.schedule_time, 'relative')}</div>
                                    <div className="text-xs text-gray-500">
                                      {formatIndiaTime(flow.schedule_config.schedule_time, 'time-ampm')}
                                    </div>
                                    {/* Show message for flows that have passed their scheduled time */}
                                    {flow.status === 'active' && isPastIndiaTime(flow.schedule_config.schedule_time) && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <Clock className="h-3 w-3 text-gray-400" />
                                        <span className="text-gray-500 text-xs">Scheduled time has passed</span>
                                      </div>
                                    )}
                                    {/* Countdown timer for active flows */}
                                    {flow.status === 'active' && countdowns[flow.id] && (
                                      <div className="flex items-center gap-1 mt-1 group relative">
                                        <Clock className={`h-3 w-3 ${countdowns[flow.id].days === 0 && countdowns[flow.id].hours === 0 && countdowns[flow.id].minutes < 5
                                          ? 'text-red-500 animate-pulse'
                                          : 'text-orange-500'
                                          }`} />
                                        <span className={`font-medium text-xs ${countdowns[flow.id].days === 0 && countdowns[flow.id].hours === 0 && countdowns[flow.id].minutes < 5
                                          ? 'text-red-600'
                                          : 'text-orange-600'
                                          }`}>
                                          {countdowns[flow.id].days > 0 && `${countdowns[flow.id].days}d `}
                                          {countdowns[flow.id].hours > 0 && `${countdowns[flow.id].hours}h `}
                                          {countdowns[flow.id].minutes > 0 && `${countdowns[flow.id].minutes}m `}
                                          {countdowns[flow.id].seconds}s
                                        </span>
                                        {/* Tooltip */}
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                          Next run: {formatLocalTime(countdowns[flow.id].nextExecution)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <div>
                                      Cron: {(() => {
                                        // Convert UTC cron expression to local time for display
                                        try {
                                          const parts = flow.schedule_config.cron_expr.split(' ');
                                          if (parts.length === 5) {
                                            const [min, hour] = parts;
                                            // Create UTC date
                                            const utcDate = new Date();
                                            utcDate.setUTCHours(parseInt(hour), parseInt(min), 0, 0);
                                            // Get local time equivalent
                                            const localHours = utcDate.getHours();
                                            const localMinutes = utcDate.getMinutes();
                                            return `${localMinutes.toString().padStart(2, '0')} ${localHours.toString().padStart(2, '0')} ${parts[2]} ${parts[3]} ${parts[4]}`;
                                          }
                                        } catch (e) {
                                          // Fallback to original if parsing fails
                                        }
                                        return flow.schedule_config.cron_expr;
                                      })()}
                                    </div>
                                    {/* Countdown timer for cron flows */}
                                    {flow.status === 'active' && countdowns[flow.id] && (
                                      <div className="flex items-center gap-1 mt-1 group relative">
                                        <Clock className={`h-3 w-3 ${countdowns[flow.id].days === 0 && countdowns[flow.id].hours === 0 && countdowns[flow.id].minutes < 5
                                          ? 'text-red-500 animate-pulse'
                                          : 'text-orange-500'
                                          }`} />
                                        <span className={`font-medium text-xs ${countdowns[flow.id].days === 0 && countdowns[flow.id].hours === 0 && countdowns[flow.id].minutes < 5
                                          ? 'text-red-600'
                                          : 'text-orange-600'
                                          }`}>
                                          {countdowns[flow.id].days > 0 && `${countdowns[flow.id].days}d `}
                                          {countdowns[flow.id].hours > 0 && `${countdowns[flow.id].hours}h `}
                                          {countdowns[flow.id].minutes > 0 && `${countdowns[flow.id].minutes}m `}
                                          {countdowns[flow.id].seconds}s
                                        </span>
                                        {/* Tooltip */}
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                          Next run: {formatLocalTime(countdowns[flow.id].nextExecution)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {flow.created_at ? formatIndiaTime(flow.created_at, 'relative') : 'Unknown'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {flow.created_by_name ? (
                            <div>
                              <div className="font-medium text-gray-900">{flow.created_by_name}</div>
                              {flow.created_by_email && (
                                <div className="text-xs text-gray-500">{flow.created_by_email}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/flows/${flow.id}/edit`)}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Edit Campaign"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {(() => {
                              const playPause = getFlowPlayPauseAction(flow);
                              if (!playPause.show) return null;
                              return (
                                <button
                                  onClick={() => handleToggleFlow(flow)}
                                  className={`p-2 transition-colors ${playPause.mode === 'pause'
                                    ? 'text-yellow-600 hover:text-yellow-700'
                                    : 'text-green-600 hover:text-green-700'
                                    }`}
                                  title={playPause.mode === 'pause' ? 'Pause Campaign' : 'Activate Campaign'}
                                >
                                  {playPause.mode === 'pause' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                </button>
                              );
                            })()}
                            <button
                              onClick={() => fetchFlowLogs(flow.id)}
                              disabled={logsLoading}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                              title="View Campaign Logs"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteFlow(flow.id)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete Campaign"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="px-6 py-4 border-t bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} campaigns
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-1 text-sm rounded ${pagination.page === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Flow Logs Modal */}
      {showFlowLogs && flowLogs && (
        <ModalPortal>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                📊 Campaign Logs: {flowLogs.flow.name}
              </h3>
              <button
                onClick={() => setShowFlowLogs(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-600">Total Executions</p>
                  <p className="text-2xl font-bold text-blue-900">{flowLogs.summary.total_executions || 0}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-green-600">Messages Sent</p>
                  <p className="text-2xl font-bold text-green-900">{flowLogs.summary.messages_sent || 0}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-red-600">Messages Failed</p>
                  <p className="text-2xl font-bold text-red-900">{flowLogs.summary.messages_failed || 0}</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-yellow-600">Errors</p>
                  <p className="text-2xl font-bold text-yellow-900">{flowLogs.summary.errors || 0}</p>
                </div>
              </div>

              {/* Logs */}
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-medium text-gray-900">Execution Logs</h4>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={deleteAllFlowLogs}
                      disabled={deletingLogs || flowLogs.logs.length === 0}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deletingLogs ? 'Deleting...' : '🗑️ Delete All'}
                    </button>
                    <span className="text-sm text-gray-500">
                      Page {flowLogs.pagination.page} of {flowLogs.pagination.pages}
                      ({flowLogs.pagination.total} total)
                    </span>
                  </div>
                </div>

                {flowLogs.logs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No logs found for this campaign</p>
                ) : (
                  <>
                    {flowLogs.logs.map((log) => (
                      <div key={log.id} className={`p-4 rounded-lg border-l-4 ${log.logLevel === 'error' ? 'bg-red-50 border-red-400' :
                        log.logLevel === 'warning' ? 'bg-yellow-50 border-yellow-400' :
                          log.logLevel === 'success' ? 'bg-green-50 border-green-400' :
                            'bg-gray-50 border-gray-400'
                        }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className={`px-2 py-1 text-xs rounded-full ${log.logLevel === 'error' ? 'bg-red-100 text-red-800' :
                                log.logLevel === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                  log.logLevel === 'success' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                {log.logLevel}
                              </span>
                              <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                {log.logType}
                              </span>
                              {log.contactName && (
                                <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                                  {log.contactName}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-900 mb-1">{log.message}</p>
                            {log.details && (
                              <details className="text-xs text-gray-600">
                                <summary className="cursor-pointer hover:text-gray-800">Details</summary>
                                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <div className="text-xs text-gray-500">
                              <p>{formatLocalTime(log.createdAt || log.created_at)}</p>
                              {log.executionId && (
                                <p>Exec: {log.executionId}</p>
                              )}
                            </div>
                            <button
                              onClick={() => deleteLog(log.id)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                              title="Delete this log"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Pagination Controls */}
                    {flowLogs.pagination.pages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => fetchFlowLogs(selectedFlowId, currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                          >
                            Previous
                          </button>
                          <span className="text-sm text-gray-600">
                            Page {currentPage} of {flowLogs.pagination.pages}
                          </span>
                          <button
                            onClick={() => fetchFlowLogs(selectedFlowId, currentPage + 1)}
                            disabled={currentPage === flowLogs.pagination.pages}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                          >
                            Next
                          </button>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Show:</span>
                          <select
                            value={logsPerPage}
                            onChange={(e) => {
                              setLogsPerPage(parseInt(e.target.value));
                              fetchFlowLogs(selectedFlowId, 1);
                            }}
                            className="px-2 py-1 text-sm border rounded"
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

    </div>
  );
};

export default Flows;
